/**
 * BatchModals — Set Systems + Set Locations batch-assignment modals for SitesPage.
 * NOTE: these two share a modal family targeted by a separate cross-viewer unification (phase 3.2, on hold);
 * moved verbatim here, to be swapped for the shared component in a later slice.
 *
 * JSX moved verbatim from src/components/SitesPage.tsx (SitesPage split 3/3).
 * Rendered DOM is byte-equivalent (classNames, text, conditional structure, keys unchanged).
 * All page state/handlers arrive as props; the interface + SitesPage invocation are
 * generated from a single prop list so they cannot drift.
 */
import React from 'react';

interface BatchModalsProps {
  addSystemsSubcategoryLevel: () => void;
  isApplyingLocations: boolean;
  isApplyingSystems: boolean;
  isLoadingLocationsProperties: boolean;
  isLoadingSystemsProperties: boolean;
  locationsAvailableProperties: string[];
  locationsResult: { updated: number; unchanged: number; failed: number } | null;
  removeSystemsSubcategoryLevel: (index: number) => void;
  selectedPOIs: Set<string>;
  selectedSpaceProperty: string;
  selectedStoreyProperty: string;
  selectedSystemProperty: string;
  showSetLocationsModal: boolean;
  showSetSystemsModal: boolean;
  spacePropertySearch: string;
  storeyPropertySearch: string;
  systemPropertySearch: string;
  systemsAvailableProperties: string[];
  systemsPropertyValues: Record<string, string[]>;
  systemsResult: { updated: number; unchanged: number; failed: number } | null;
  systemsSubcategoryLevels: string[];
  systemsSubcategorySearches: string[];
  setLocationsResult: React.Dispatch<React.SetStateAction<{ updated: number; unchanged: number; failed: number } | null>>;
  setSelectedSpaceProperty: React.Dispatch<React.SetStateAction<string>>;
  setSelectedStoreyProperty: React.Dispatch<React.SetStateAction<string>>;
  setSelectedSystemProperty: React.Dispatch<React.SetStateAction<string>>;
  setShowSetLocationsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSetSystemsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSpacePropertySearch: React.Dispatch<React.SetStateAction<string>>;
  setStoreyPropertySearch: React.Dispatch<React.SetStateAction<string>>;
  setSystemPropertySearch: React.Dispatch<React.SetStateAction<string>>;
  setSystemsResult: React.Dispatch<React.SetStateAction<{ updated: number; unchanged: number; failed: number } | null>>;
  setSystemsSubcategoryLevels: React.Dispatch<React.SetStateAction<string[]>>;
  setSystemsSubcategorySearches: React.Dispatch<React.SetStateAction<string[]>>;
  handleApplyLocationsBatch: () => Promise<void>;
  handleApplySystemsBatch: () => Promise<void>;
}

const BatchModals: React.FC<BatchModalsProps> = ({
  addSystemsSubcategoryLevel,
  isApplyingLocations,
  isApplyingSystems,
  isLoadingLocationsProperties,
  isLoadingSystemsProperties,
  locationsAvailableProperties,
  locationsResult,
  removeSystemsSubcategoryLevel,
  selectedPOIs,
  selectedSpaceProperty,
  selectedStoreyProperty,
  selectedSystemProperty,
  showSetLocationsModal,
  showSetSystemsModal,
  spacePropertySearch,
  storeyPropertySearch,
  systemPropertySearch,
  systemsAvailableProperties,
  systemsPropertyValues,
  systemsResult,
  systemsSubcategoryLevels,
  systemsSubcategorySearches,
  setLocationsResult,
  setSelectedSpaceProperty,
  setSelectedStoreyProperty,
  setSelectedSystemProperty,
  setShowSetLocationsModal,
  setShowSetSystemsModal,
  setSpacePropertySearch,
  setStoreyPropertySearch,
  setSystemPropertySearch,
  setSystemsResult,
  setSystemsSubcategoryLevels,
  setSystemsSubcategorySearches,
  handleApplyLocationsBatch,
  handleApplySystemsBatch,
}) => {
  return (
    <>
      {/* Set Systems Modal */}
      {showSetSystemsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h3>Set Systems</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                Apply system hierarchy to {selectedPOIs.size} selected element{selectedPOIs.size > 1 ? 's' : ''}.
              </p>

              {isLoadingSystemsProperties ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Loading available properties...
                </div>
              ) : (
                <>
                  {/* System Property (required) */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: 'var(--primary-color)', fontSize: '14px' }}>
                      System Property (required):
                    </label>
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={systemPropertySearch}
                      onChange={(e) => setSystemPropertySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px 4px 0 0',
                        fontSize: '13px',
                        backgroundColor: 'var(--background-color)',
                        color: 'var(--text-primary)',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{
                      width: '100%',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      border: '1px solid var(--border-color)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      backgroundColor: 'var(--background-color)'
                    }}>
                      {systemsAvailableProperties
                        .filter(prop => prop.toLowerCase().includes(systemPropertySearch.toLowerCase()))
                        .slice(0, 50)
                        .map(prop => (
                          <div
                            key={prop}
                            onClick={() => setSelectedSystemProperty(prop)}
                            style={{
                              padding: '6px 12px',
                              cursor: 'pointer',
                              backgroundColor: selectedSystemProperty === prop ? 'rgba(72, 202, 228, 0.2)' : 'transparent',
                              color: 'var(--text-primary)',
                              fontSize: '13px'
                            }}
                          >
                            {prop} ({systemsPropertyValues[prop]?.length || 0} values)
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Dynamic Subcategory Levels */}
                  <div style={{ borderBottom: '1px solid var(--border-color)', margin: '16px 0', position: 'relative' }}>
                    <span style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', backgroundColor: 'var(--surface-color)', padding: '0 8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Subcategories (optional)
                    </span>
                  </div>

                  {systemsSubcategoryLevels.map((level, index) => (
                    <div key={index} style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <label style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '13px' }}>
                          Subcategory {index + 1}:
                        </label>
                        {systemsSubcategoryLevels.length > 1 && (
                          <button onClick={() => removeSystemsSubcategoryLevel(index)} style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', fontSize: '12px' }}>
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Search properties..."
                        value={systemsSubcategorySearches[index] || ''}
                        onChange={(e) => {
                          const newSearches = [...systemsSubcategorySearches];
                          newSearches[index] = e.target.value;
                          setSystemsSubcategorySearches(newSearches);
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px 4px 0 0',
                          fontSize: '13px',
                          backgroundColor: 'var(--background-color)',
                          color: 'var(--text-primary)',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{
                        width: '100%',
                        maxHeight: '100px',
                        overflowY: 'auto',
                        border: '1px solid var(--border-color)',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        backgroundColor: 'var(--background-color)'
                      }}>
                        <div
                          onClick={() => {
                            const newLevels = [...systemsSubcategoryLevels];
                            newLevels[index] = '';
                            setSystemsSubcategoryLevels(newLevels);
                          }}
                          style={{ padding: '6px 12px', cursor: 'pointer', backgroundColor: level === '' ? 'rgba(72, 202, 228, 0.2)' : 'transparent', color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}
                        >
                          -- None --
                        </div>
                        {systemsAvailableProperties
                          .filter(prop => prop.toLowerCase().includes((systemsSubcategorySearches[index] || '').toLowerCase()))
                          .slice(0, 30)
                          .map(prop => (
                            <div
                              key={prop}
                              onClick={() => {
                                const newLevels = [...systemsSubcategoryLevels];
                                newLevels[index] = prop;
                                setSystemsSubcategoryLevels(newLevels);
                              }}
                              style={{ padding: '6px 12px', cursor: 'pointer', backgroundColor: level === prop ? 'rgba(72, 202, 228, 0.2)' : 'transparent', color: 'var(--text-primary)', fontSize: '13px' }}
                            >
                              {prop}
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}

                  <button onClick={addSystemsSubcategoryLevel} className="btn btn-secondary" style={{ width: '100%', marginBottom: '16px', fontSize: '13px' }}>
                    + Add Subcategory Level
                  </button>

                  {systemsResult && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: systemsResult.updated > 0 ? 'rgba(40, 167, 69, 0.1)' : 'var(--background-color)',
                      border: `1px solid ${systemsResult.updated > 0 ? '#28a745' : 'var(--border-color)'}`,
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: 500, marginBottom: '8px', color: 'var(--text-primary)' }}>Assignment Complete</div>
                      <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                        <div>Updated: <strong>{systemsResult.updated}</strong></div>
                        <div>Unchanged: <strong>{systemsResult.unchanged}</strong></div>
                        {systemsResult.failed > 0 && <div style={{ color: 'var(--danger-color)' }}>Failed: <strong>{systemsResult.failed}</strong></div>}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button className="btn btn-secondary" onClick={() => { setShowSetSystemsModal(false); setSystemsResult(null); }}>
                  {systemsResult ? 'Close' : 'Cancel'}
                </button>
                {!systemsResult && (
                  <button className="btn btn-primary" onClick={handleApplySystemsBatch} disabled={isApplyingSystems || !selectedSystemProperty}>
                    {isApplyingSystems ? 'Applying...' : 'Apply'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Locations Modal */}
      {showSetLocationsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h3>Set Locations</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                Apply location assignment to {selectedPOIs.size} selected element{selectedPOIs.size > 1 ? 's' : ''}.
              </p>

              {isLoadingLocationsProperties ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Loading available properties...
                </div>
              ) : (
                <>
                  {/* Storey Property */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--text-primary)' }}>
                      Storey Property:
                    </label>
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={storeyPropertySearch}
                      onChange={(e) => setStoreyPropertySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px 4px 0 0',
                        fontSize: '13px',
                        backgroundColor: 'var(--background-color)',
                        color: 'var(--text-primary)',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{
                      width: '100%',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      border: '1px solid var(--border-color)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      backgroundColor: 'var(--background-color)'
                    }}>
                      <div
                        onClick={() => setSelectedStoreyProperty('')}
                        style={{ padding: '6px 12px', cursor: 'pointer', backgroundColor: selectedStoreyProperty === '' ? 'rgba(72, 202, 228, 0.2)' : 'transparent', color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}
                      >
                        -- None --
                      </div>
                      {locationsAvailableProperties
                        .filter(prop => prop.toLowerCase().includes(storeyPropertySearch.toLowerCase()))
                        .slice(0, 50)
                        .map(prop => (
                          <div
                            key={prop}
                            onClick={() => setSelectedStoreyProperty(prop)}
                            style={{ padding: '6px 12px', cursor: 'pointer', backgroundColor: selectedStoreyProperty === prop ? 'rgba(72, 202, 228, 0.2)' : 'transparent', color: 'var(--text-primary)', fontSize: '13px' }}
                          >
                            {prop}
                          </div>
                        ))}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Property matching Building Storey names
                    </div>
                  </div>

                  {/* Space Property */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--text-primary)' }}>
                      Space Property:
                    </label>
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={spacePropertySearch}
                      onChange={(e) => setSpacePropertySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px 4px 0 0',
                        fontSize: '13px',
                        backgroundColor: 'var(--background-color)',
                        color: 'var(--text-primary)',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{
                      width: '100%',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      border: '1px solid var(--border-color)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      backgroundColor: 'var(--background-color)'
                    }}>
                      <div
                        onClick={() => setSelectedSpaceProperty('')}
                        style={{ padding: '6px 12px', cursor: 'pointer', backgroundColor: selectedSpaceProperty === '' ? 'rgba(72, 202, 228, 0.2)' : 'transparent', color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}
                      >
                        -- None --
                      </div>
                      {locationsAvailableProperties
                        .filter(prop => prop.toLowerCase().includes(spacePropertySearch.toLowerCase()))
                        .slice(0, 50)
                        .map(prop => (
                          <div
                            key={prop}
                            onClick={() => setSelectedSpaceProperty(prop)}
                            style={{ padding: '6px 12px', cursor: 'pointer', backgroundColor: selectedSpaceProperty === prop ? 'rgba(72, 202, 228, 0.2)' : 'transparent', color: 'var(--text-primary)', fontSize: '13px' }}
                          >
                            {prop}
                          </div>
                        ))}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Property matching Space/Room names or codes
                    </div>
                  </div>

                  {locationsResult && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: locationsResult.updated > 0 ? 'rgba(40, 167, 69, 0.1)' : 'var(--background-color)',
                      border: `1px solid ${locationsResult.updated > 0 ? '#28a745' : 'var(--border-color)'}`,
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: 500, marginBottom: '8px', color: 'var(--text-primary)' }}>Assignment Complete</div>
                      <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                        <div>Updated: <strong>{locationsResult.updated}</strong></div>
                        <div>Unchanged: <strong>{locationsResult.unchanged}</strong></div>
                        {locationsResult.failed > 0 && <div style={{ color: 'var(--danger-color)' }}>Failed: <strong>{locationsResult.failed}</strong></div>}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button className="btn btn-secondary" onClick={() => { setShowSetLocationsModal(false); setLocationsResult(null); }}>
                  {locationsResult ? 'Close' : 'Cancel'}
                </button>
                {!locationsResult && (
                  <button className="btn btn-primary" onClick={handleApplyLocationsBatch} disabled={isApplyingLocations || (!selectedStoreyProperty && !selectedSpaceProperty)}>
                    {isApplyingLocations ? 'Applying...' : 'Apply'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BatchModals;
